/**
 * C5 Tier 1 — the per-endpoint circuit registry.
 *
 * Replaces the old single module-global `circuitOpen` (the motivating bug: one
 * endpoint's 5xx storm froze the whole app for 60s). The topological insight
 * (C5 design §1): a RESPONSE — even 500/503/409 — proves the transport worked
 * and the server is reachable, so a response-bearing failure is scoped to THAT
 * request/endpoint and must gate nothing else. Only breadth across DISTINCT
 * endpoints (handled by the connectivity controller) can be app-wide.
 *
 * This module owns exactly one concern: "should we stop hammering THIS
 * endpoint key, and when may we probe it again." It counts consecutive
 * response-bearing 5xx/retryable failures per `${METHOD} ${normalizeRoute}`
 * key; ANY success on the key resets it (a single poison request among healthy
 * siblings never opens it). Recovery is a half-open SINGLE probe after a
 * growing backoff — not a blind timer — so a still-broken endpoint is probed
 * once with widening gaps and recovers instantly when a fix ships.
 *
 * 4xx (400/401/403/409/422) are terminal and request-specific: the interceptor
 * never routes them here (no circuit, no retry) — the C2 principle that the
 * review-mode 409 must gate nothing.
 */

export interface CircuitRegistryConfig {
  /** Production resilience; disabled under test (mirrors the old breaker). */
  enabled: boolean;
  /** Consecutive response-bearing 5xx on a key before it opens. */
  failureThreshold: number;
  /** Half-open backoff ladder (ms) before each single probe. */
  backoffScheduleMs: number[];
  /** Ceiling for the ladder once it is exhausted. */
  backoffCapMs: number;
  /** Fractional jitter added to each backoff (thundering-herd guard on recovery). */
  jitterRatio: number;
}

let config: CircuitRegistryConfig = {
  enabled: true,
  failureThreshold: 5,
  backoffScheduleMs: [5000, 15000, 45000, 120000],
  backoffCapMs: 300000,
  jitterRatio: 0.25,
};

export function configureCircuitRegistry(overrides: Partial<CircuitRegistryConfig>): void {
  config = { ...config, ...overrides };
}

interface Circuit {
  /** Consecutive response-bearing 5xx since the last success. */
  failures: number;
  /** True once `failures >= threshold`; short-circuits the key. */
  open: boolean;
  /** How many times we have re-opened (drives the backoff ladder). */
  reopenLevel: number;
  /** Epoch ms after which a single half-open probe may pass. */
  nextProbeAt: number;
  /** A probe has been admitted and is awaiting its result. */
  probeInFlight: boolean;
}

const circuits = new Map<string, Circuit>();

export function resetCircuitRegistry(): void {
  circuits.clear();
}

// ---------------------------------------------------------------------------
// Route normalization — collapse ids / uuids / opaque stub segments to :param
// so `PUT /api/view/shares/<stub>/` maps to a single stable circuit key. This
// is a pragmatic heuristic (no orval route table wired here yet — a clean seam
// to swap in later); worst case two routes share or split a key, which only
// affects breaker granularity, never correctness of a response.
// ---------------------------------------------------------------------------

const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

function isParamSegment(seg: string): boolean {
  if (seg === '') return false;
  if (/^\d+$/.test(seg)) return true; // numeric id
  if (UUID_RE.test(seg)) return true; // uuid
  if (/^[0-9a-f]{8,}$/i.test(seg) && /\d/.test(seg)) return true; // hex hash with a digit
  // Long opaque token (share stub): mixed letters+digits, length >= 12.
  if (seg.length >= 12 && /\d/.test(seg) && /[a-zA-Z]/.test(seg)) return true;
  return false;
}

export function normalizeRoute(url: string): string {
  const path = (url.split('#')[0] ?? '').split('?')[0] ?? '';
  return path
    .split('/')
    .map((seg) => (isParamSegment(seg) ? ':param' : seg))
    .join('/');
}

export function circuitKey(method: string, url: string): string {
  return `${method.toUpperCase()} ${normalizeRoute(url)}`;
}

function backoffFor(reopenLevel: number): number {
  const ladder = config.backoffScheduleMs;
  const base = reopenLevel < ladder.length ? ladder[reopenLevel]! : config.backoffCapMs;
  const capped = Math.min(base, config.backoffCapMs);
  return capped + capped * config.jitterRatio * Math.random();
}

function get(method: string, url: string): Circuit | undefined {
  return circuits.get(circuitKey(method, url));
}

function ensure(method: string, url: string): Circuit {
  const key = circuitKey(method, url);
  let c = circuits.get(key);
  if (!c) {
    c = { failures: 0, open: false, reopenLevel: 0, nextProbeAt: 0, probeInFlight: false };
    circuits.set(key, c);
  }
  return c;
}

/** A response-bearing 5xx/retryable failure on a key. */
export function recordEndpointFailure(method: string, url: string): void {
  if (!config.enabled) return;
  const c = ensure(method, url);

  if (c.open) {
    // This was the half-open probe and it failed → widen the backoff.
    c.probeInFlight = false;
    c.reopenLevel += 1;
    c.nextProbeAt = Date.now() + backoffFor(c.reopenLevel);
    return;
  }

  c.failures += 1;
  if (c.failures >= config.failureThreshold) {
    c.open = true;
    c.reopenLevel = 0;
    c.nextProbeAt = Date.now() + backoffFor(0);
    c.probeInFlight = false;
  }
}

/** ANY success on a key closes and fully resets it. */
export function recordEndpointSuccess(method: string, url: string): void {
  if (!config.enabled) return;
  const c = get(method, url);
  if (!c) return;
  c.failures = 0;
  c.open = false;
  c.reopenLevel = 0;
  c.nextProbeAt = 0;
  c.probeInFlight = false;
}

export type Admission = 'pass' | 'probe' | 'blocked';

/**
 * Admission gate for the request interceptor. MUTATING: when the backoff has
 * elapsed it admits exactly ONE request as the half-open probe (marking
 * `probeInFlight`) and says so, so the caller can stamp the request and settle
 * it on its terminal exit (`releaseProbe`). Everything else on an open key is
 * blocked until that probe resolves.
 */
export function admitRequest(method: string, url: string): Admission {
  if (!config.enabled) return 'pass';
  const c = get(method, url);
  if (!c || !c.open) return 'pass';
  if (!c.probeInFlight && Date.now() >= c.nextProbeAt) {
    c.probeInFlight = true; // admit this one as the probe
    return 'probe';
  }
  return 'blocked';
}

/**
 * Settle the half-open probe on a key. A no-op unless the key is open with a
 * probe in flight, so a stale or repeated call cannot disturb a later probe.
 * `'success'` closes the key; `'failure'` re-arms it with a wider backoff.
 */
export function releaseProbe(method: string, url: string, outcome: 'success' | 'failure'): void {
  if (!config.enabled) return;
  const c = get(method, url);
  if (!c || !c.open || !c.probeInFlight) return;
  if (outcome === 'success') recordEndpointSuccess(method, url);
  else recordEndpointFailure(method, url);
}

/** Non-mutating: is this key's circuit currently open? (retry gating, tests) */
export function isEndpointCircuitOpen(method: string, url: string): boolean {
  const c = get(method, url);
  return c?.open ?? false;
}

/** Is ANY endpoint circuit open? Backs the legacy `isApiCircuitOpen()` shim. */
export function anyCircuitOpen(): boolean {
  for (const c of circuits.values()) if (c.open) return true;
  return false;
}
